// src/indexer/EntityResolver.js

/**
 * Entity resolver for EventIndexer.
 * Provides the Entities API for domain-level queries.
 */
class EntityResolver {
  constructor(queryEngine, entityDefinitions = {}) {
    this.queryEngine = queryEngine;
    this.definitions = entityDefinitions;
    this.entityAPIs = {};

    // Create API for each entity
    for (const [name, definition] of Object.entries(entityDefinitions)) {
      this.entityAPIs[name] = this._createEntityAPI(name, definition);
    }
  }

  /**
   * Get entity API by name.
   * @param {string} name - Entity name
   * @returns {EntityQueryable}
   */
  getEntity(name) {
    return this.entityAPIs[name];
  }

  /**
   * Get all entity APIs (for proxy access).
   * @returns {Object}
   */
  getAllEntities() {
    return this.entityAPIs;
  }

  _createEntityAPI(name, definition) {
    const self = this;

    return {
      /**
       * Query entities.
       * @param {EntityWhereClause} where - Filter conditions
       * @returns {Promise<Entity[]>}
       */
      async query(where = {}) {
        return self._queryEntities(name, definition, where);
      },

      /**
       * Get single entity by key.
       * @param {string|number} key - Entity key value
       * @returns {Promise<Entity|null>}
       */
      async get(key) {
        const entities = await self._queryEntities(name, definition, {
          [definition.key]: key
        });
        return entities[0] || null;
      },

      /**
       * Subscribe to entity changes.
       * @param {EntityWhereClause} where - Filter conditions
       * @param {Function} callback - Called when entities change
       * @returns {Function} Unsubscribe function
       */
      subscribe(where, callback) {
        // Subscribe to source event and related events
        const eventTypes = self._getRelevantEventTypes(definition);

        return self.queryEngine.subscribe(eventTypes, async () => {
          const entities = await self._queryEntities(name, definition, where);
          callback(entities);
        });
      },

      /**
       * Count entities.
       * @param {EntityWhereClause} where - Filter conditions
       * @returns {Promise<number>}
       */
      async count(where = {}) {
        const entities = await self._queryEntities(name, definition, where);
        return entities.length;
      }
    };
  }

  async _queryEntities(name, definition, where = {}) {
    // Separate status filter from direct filters
    const statusFilter = where.status;
    const directFilters = { ...where };
    delete directFilters.status;

    // Query source events
    const sourceResult = await this.queryEngine.query(definition.source, {
      where: directFilters,
      limit: 10000 // Get all matching source events
    });

    // Get event checker for status/computed evaluation
    const eventChecker = await this.queryEngine.getEventChecker();

    // Prefetch events needed for status evaluation
    const relevantEventTypes = this._getRelevantEventTypes(definition);
    await eventChecker.prefetch(relevantEventTypes);

    // Build entities from source events
    const entities = [];
    for (const sourceEvent of sourceResult.events) {
      const entity = await this._buildEntity(name, definition, sourceEvent, eventChecker);

      // Apply status filter
      if (statusFilter && entity.status !== statusFilter) {
        continue;
      }

      entities.push(entity);
    }

    return entities;
  }

  async _buildEntity(name, definition, sourceEvent, eventChecker) {
    const entity = {
      _type: name,
      _sourceEvent: sourceEvent,
      ...sourceEvent.data
    };

    // Add key field explicitly
    entity[definition.key] = sourceEvent.data[definition.key] || sourceEvent.indexed[definition.key];

    // Evaluate status
    if (definition.status) {
      entity.status = this._evaluateStatus(entity, definition.status, eventChecker);
    }

    // Evaluate computed fields
    if (definition.computed) {
      for (const [fieldName, computeFn] of Object.entries(definition.computed)) {
        try {
          entity[fieldName] = computeFn(entity, eventChecker);
        } catch (error) {
          console.error(`[EntityResolver] Error computing ${fieldName}:`, error);
          entity[fieldName] = null;
        }
      }
    }

    // Resolve relations (lazy - only resolve when accessed)
    if (definition.relations) {
      for (const [relationName, relationDef] of Object.entries(definition.relations)) {
        Object.defineProperty(entity, relationName, {
          get: () => this._resolveRelation(entity, relationDef),
          enumerable: true
        });
      }
    }

    return entity;
  }

  _evaluateStatus(entity, statusDef, eventChecker) {
    // Check each status condition in order
    for (const [statusName, condition] of Object.entries(statusDef)) {
      if (statusName === 'default') continue;

      try {
        if (condition(entity, eventChecker)) {
          return statusName;
        }
      } catch (error) {
        console.error(`[EntityResolver] Error evaluating status ${statusName}:`, error);
      }
    }

    return statusDef.default || 'unknown';
  }

  async _resolveRelation(entity, relationDef) {
    const relatedEntity = this.entityAPIs[relationDef.entity];
    if (!relatedEntity) return null;

    const foreignKeyValue = entity[relationDef.foreignKey];
    if (foreignKeyValue === undefined) return null;

    if (relationDef.type === 'many') {
      return relatedEntity.query({ [relationDef.foreignKey]: foreignKeyValue });
    } else {
      return relatedEntity.get(foreignKeyValue);
    }
  }

  _getRelevantEventTypes(definition) {
    const types = new Set([definition.source]);

    // Add events referenced in status conditions
    if (definition.status) {
      for (const condition of Object.values(definition.status)) {
        if (typeof condition === 'function') {
          // Try to extract event names from function source
          // This is a best-effort heuristic
          const fnStr = condition.toString();
          const matches = fnStr.match(/events\.has\(['"](\w+)['"]/g) || [];
          for (const match of matches) {
            const eventName = match.match(/['"](\w+)['"]/)?.[1];
            if (eventName) types.add(eventName);
          }
        }
      }
    }

    return Array.from(types);
  }
}

export default EntityResolver;
